import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink,
  PaginationNext, PaginationPrevious,
} from "@/components/ui/pagination";
import { fmtDateTime } from "@/lib/format";
import { ShieldCheck } from "lucide-react";

const ACTION_COLORS = {
  login: "bg-blue-50 text-blue-700 border-blue-200",
  logout: "bg-slate-50 text-slate-700 border-slate-200",
  create: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-amber-50 text-amber-700 border-amber-200",
  soft_delete: "bg-red-50 text-red-700 border-red-200",
  deactivate: "bg-red-50 text-red-700 border-red-200",
  bulk_import: "bg-violet-50 text-violet-700 border-violet-200",
};

export default function AuditPage() {
  const [list, setList] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const load = async (p = page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit", { params: { page: p, page_size: pageSize } });
      setList(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(page); /* eslint-disable-next-line */ }, [page]);

  const totalPages = Math.max(1, Math.ceil(list.total / pageSize));

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Compliance</div>
          <h1 className="font-display text-3xl font-semibold flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-[#234935]" /> Auditoria
          </h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground">
            Trilha completa de eventos do sistema · {list.total.toLocaleString("pt-BR")} registros
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[170px]">Data/Hora</TableHead>
              <TableHead>Usuário</TableHead>
              <TableHead>Ação</TableHead>
              <TableHead>Entidade</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-5" /></TableCell></TableRow>)
            ) : list.items.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Sem eventos.</TableCell></TableRow>
            ) : list.items.map((r) => (
              <TableRow key={r.id} className="rh-row">
                <TableCell className="font-mono text-[12px]">{fmtDateTime(r.at)}</TableCell>
                <TableCell className="text-[12.5px]">
                  <div className="font-medium">{r.user_name || "—"}</div>
                  <div className="text-[11px] text-muted-foreground">{r.user_email}</div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={`ring-1 ring-inset ${ACTION_COLORS[r.action] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                    {r.action}
                  </Badge>
                </TableCell>
                <TableCell className="text-[12.5px]">{r.entity}</TableCell>
                <TableCell className="font-mono text-[11px] text-muted-foreground max-w-[160px] truncate">{r.entity_id}</TableCell>
                <TableCell className="font-mono text-[11px] text-muted-foreground">{r.ip || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between border-t border-border px-4 py-3">
          <div className="text-[12px] text-muted-foreground">Página {page} de {totalPages}</div>
          <Pagination>
            <PaginationContent>
              <PaginationItem><PaginationPrevious href="#" onClick={(e) => { e.preventDefault(); if (page > 1) setPage(page - 1); }} /></PaginationItem>
              <PaginationItem><PaginationLink href="#" isActive>{page}</PaginationLink></PaginationItem>
              <PaginationItem><PaginationNext href="#" onClick={(e) => { e.preventDefault(); if (page < totalPages) setPage(page + 1); }} /></PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
    </div>
  );
}
